import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout.jsx';
import { api, uploadUrl } from '../../api/client.js';
import { Swal2 } from '../../lib/swal.js';
import { MORPH_BTN_SM } from '../../lib/ui.js';

const FILTERS = [
  { id: 'all', label: 'Semua', icon: 'apps' },
  { id: 'active', label: 'Aktif', icon: 'visibility' },
  { id: 'inactive', label: 'Tidak Aktif', icon: 'visibility_off' },
];

// Bold-kan bagian teks yang cocok dengan kata pencarian
function highlightMatch(text, query) {
  if (!text) return text;
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <b className="font-bold text-neutral-900 dark:text-white">{text.slice(idx, idx + query.length)}</b>
      {text.slice(idx + query.length)}
    </>
  );
}

export default function TestimonialList() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState('all');
  const [filterOpen, setFilterOpen] = useState(false);
  const filterMenuRef = useRef(null);

  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const searchBoxRef = useRef(null);

  const load = () => api.get('/admin/testimonial').then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);

  // Autocomplete: debounce ketik, saring dari data testimonial (dari database) yang sudah dimuat
  useEffect(() => {
    const q = searchInput.trim().toLowerCase();
    if (!q || !items) { setSuggestions([]); return; }
    const t = setTimeout(() => {
      const matches = items.filter((it) => it.nama?.toLowerCase().includes(q) || it.peran?.toLowerCase().includes(q) || it.review?.toLowerCase().includes(q));
      setSuggestions(matches.slice(0, 6));
      setActiveIdx(-1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, items]);

  // Tutup dropdown filter/search saat klik di luar
  useEffect(() => {
    const onClickOutside = (e) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) setFilterOpen(false);
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowSuggest(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const pickSuggestion = (t) => {
    setSearchInput(t.nama);
    setSearch(t.nama);
    setShowSuggest(false);
  };

  const onSearchKeyDown = (e) => {
    if (!showSuggest || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      pickSuggestion(suggestions[activeIdx]);
    } else if (e.key === 'Escape') {
      setShowSuggest(false);
    }
  };

  const submitSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setShowSuggest(false);
  };

  const q = search.trim().toLowerCase();
  const filtered = items
    ?.filter((t) => (filter === 'all' ? true : filter === 'active' ? t.is_active : !t.is_active))
    ?.filter((t) => (!q ? true : t.nama?.toLowerCase().includes(q) || t.peran?.toLowerCase().includes(q) || t.review?.toLowerCase().includes(q)));

  const approve = async (t) => {
    await api.post(`/admin/testimonial/${t.id}/approve`);
    Swal2.toast('success', 'Testimonial disetujui dan tampil di halaman publik.');
    load();
  };
  const reject = async (t) => {
    await api.post(`/admin/testimonial/${t.id}/reject`);
    Swal2.toast('success', 'Testimonial disembunyikan dari halaman publik.');
    load();
  };
  const remove = async (t) => {
    const confirmed = await Swal2.confirmDelete(t.nama);
    if (!confirmed) return;
    await api.delete(`/admin/testimonial/${t.id}`);
    Swal2.toast('success', 'Testimonial berhasil dihapus.');
    load();
  };

  return (
    <AdminLayout title="Kelola Testimonial">
      <div className="flex flex-wrap items-center gap-2.5 mb-5">
        {/* Filter status: ikon corong + dropdown */}
        <div ref={filterMenuRef} className="relative flex-shrink-0">
          <button
            type="button"
            onClick={() => setFilterOpen((o) => !o)}
            aria-pressed={filterOpen}
            title="Filter status"
            className={`relative flex items-center justify-center w-10 h-10 rounded-xl border ${MORPH_BTN_SM}`}
            style={{
              borderColor: filter !== 'all' ? 'var(--accent)' : 'color-mix(in oklab, var(--accent) 20%, transparent)',
              background: filter !== 'all' ? 'color-mix(in oklab, var(--accent) 10%, transparent)' : 'white',
              color: 'var(--accent)',
            }}
          >
            <span className="material-symbols-outlined text-lg">filter_alt</span>
            {filter !== 'all' && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full ring-2 ring-white dark:ring-neutral-900" style={{ background: 'var(--accent)' }} />}
          </button>

          <div className={`filter-dropdown ${filterOpen ? 'filter-dropdown-open' : 'filter-dropdown-closed'} absolute z-30 top-[calc(100%+6px)] left-0 w-44 origin-top-left rounded-xl border bg-white dark:bg-neutral-800 shadow-xl overflow-hidden p-1`} style={{ borderColor: 'color-mix(in oklab, var(--accent) 20%, transparent)' }}>
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => { setFilter(f.id); setFilterOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-left transition-colors ${filter === f.id ? '' : 'hover:bg-neutral-50 dark:hover:bg-neutral-700/50 text-neutral-600 dark:text-neutral-300'}`}
                style={filter === f.id ? { background: 'color-mix(in oklab, var(--accent) 12%, transparent)', color: 'var(--accent)' } : undefined}
              >
                <span className="material-symbols-outlined text-base">{f.icon}</span>
                {f.label}
                {filter === f.id && <span className="material-symbols-outlined text-base ml-auto">check</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Search bar */}
        <form onSubmit={submitSearch} className="flex-shrink-0">
          <div ref={searchBoxRef} className="relative">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-lg">search</span>
            <input
              value={searchInput}
              onChange={(e) => { setSearchInput(e.target.value); setShowSuggest(true); }}
              onFocus={() => { if (searchInput.trim()) setShowSuggest(true); }}
              onKeyDown={onSearchKeyDown}
              placeholder="Cari nama, peran, atau ulasan..."
              autoComplete="off"
              className="w-56 sm:w-72 h-10 pl-10 pr-4 rounded-xl border text-xs outline-none bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white border-neutral-200 dark:border-neutral-700 focus:border-forest-500 focus:ring-2 focus:ring-forest-500/20 transition-all"
            />

            {showSuggest && searchInput.trim() && (
              <div className="absolute z-30 top-[calc(100%+6px)] left-0 w-full sm:w-80 rounded-xl border bg-white dark:bg-neutral-800 shadow-xl overflow-hidden" style={{ borderColor: 'color-mix(in oklab, var(--accent) 20%, transparent)' }}>
                {suggestions.length === 0 ? (
                  <div className="px-4 py-3 text-xs text-neutral-400">Tidak ada hasil untuk "{searchInput}"</div>
                ) : (
                  suggestions.map((t, i) => (
                    <button
                      type="button"
                      key={t.id}
                      onMouseEnter={() => setActiveIdx(i)}
                      onClick={() => pickSuggestion(t)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${i === activeIdx ? 'bg-neutral-100 dark:bg-neutral-700' : 'hover:bg-neutral-50 dark:hover:bg-neutral-700/50'}`}
                    >
                      {t.avatar ? (
                        <img src={uploadUrl(t.avatar)} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-forest-500 text-white text-xs font-bold flex-shrink-0">{t.nama?.[0]?.toUpperCase()}</div>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block text-xs font-semibold text-neutral-700 dark:text-neutral-200 truncate">
                          {highlightMatch(t.nama, searchInput)}
                        </span>
                        {t.peran && (
                          <span className="block text-[11px] text-neutral-400 truncate">{highlightMatch(t.peran, searchInput)}</span>
                        )}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </form>

        <Link to="/admin/testimonial/new" className={`inline-flex items-center gap-2 px-5 h-10 rounded-xl font-bold text-white text-sm ml-auto flex-shrink-0 ${MORPH_BTN_SM}`} style={{ background: 'var(--accent)' }}>
          <span className="material-symbols-outlined text-base">add</span> Tambah Manual
        </Link>
      </div>

      <div className="space-y-3">
        {items === null && <p className="text-neutral-400">Memuat...</p>}
        {items !== null && filtered?.length === 0 && <p className="text-neutral-400">Tidak ada testimonial untuk filter/pencarian ini.</p>}
        {filtered?.map((t) => (
          <div key={t.id} className="rounded-2xl border bg-white dark:bg-neutral-900 border-black/5 dark:border-white/5 p-5 flex items-start gap-4">
            {t.avatar ? (
              <img src={uploadUrl(t.avatar)} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded-full flex items-center justify-center bg-forest-500 text-white font-bold flex-shrink-0">{t.nama?.[0]?.toUpperCase()}</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h4 className="font-bold text-sm text-neutral-900 dark:text-white">{t.nama}</h4>
                <span className="text-xs text-neutral-400">{t.peran}</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${t.source === 'client' ? 'bg-blue-50 text-blue-600' : 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800'}`}>
                  {t.source === 'client' ? 'Kiriman Klien' : 'Manual Admin'}
                </span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${t.is_active ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>
                  {t.is_active ? 'Tampil' : 'Menunggu / Disembunyikan'}
                </span>
              </div>
              <p className="text-sm text-neutral-600 dark:text-neutral-300 line-clamp-2">{t.review}</p>
              <div className="flex gap-2 mt-3">
                {!t.is_active && <button onClick={() => approve(t)} className={`text-xs font-semibold px-3 py-1.5 rounded-lg bg-green-50 text-green-600 ${MORPH_BTN_SM}`}>Setujui</button>}
                {t.is_active && <button onClick={() => reject(t)} className={`text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-50 text-amber-600 ${MORPH_BTN_SM}`}>Sembunyikan</button>}
                <Link to={`/admin/testimonial/${t.id}/edit`} className={`text-xs font-semibold px-3 py-1.5 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-white ${MORPH_BTN_SM}`}>Edit</Link>
                <button onClick={() => remove(t)} className={`text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-50 text-red-500 dark:bg-red-950/30 ${MORPH_BTN_SM}`}>Hapus</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
